import React, { useState, useEffect } from 'react';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, useDroppable, pointerWithin } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Calendar, User, Filter, X } from 'lucide-react';
import { DevOpsTask } from '../../types/devopsTasks';
import { User as UserType } from '../../types';
import { toast } from '../../utils/toast';
import { readApiError } from '../common/apiError';

interface KanbanBoardProps {
  currentUser: UserType;
  onTaskClick: (taskId: string) => void;
}

interface KanbanColumn {
  id: string;
  title: string;
  color: string;
  bgColor: string;
}

const columns: KanbanColumn[] = [
  { id: 'backlog', title: 'Backlog', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  { id: 'todo', title: 'To Do', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  { id: 'in-progress', title: 'In Progress', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  { id: 'review', title: 'Review', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  { id: 'done', title: 'Done', color: 'text-green-700', bgColor: 'bg-green-100' }
];

const priorityColors = {
  low: 'bg-green-100 text-green-800 border-green-300',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  critical: 'bg-red-100 text-red-800 border-red-300'
};

interface TaskCardProps {
  task: DevOpsTask;
  onClick: () => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: task._id ?? '' });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      onClick={onClick}
      className="bg-white rounded-lg shadow-md p-4 mb-3 border-2 border-gray-200 hover:border-blue-400 hover:shadow-lg transition-all cursor-pointer"
    >
      {/* Drag handle - hold and drag from here */}
      <div {...listeners} className="flex items-center justify-between mb-2 p-1 -m-1 rounded cursor-grab active:cursor-grabbing hover:bg-gray-50">
        <span className="text-xs font-bold text-gray-500">#{task.taskNumber} 📌</span>
        <span className={`px-2 py-1 rounded text-xs font-bold uppercase border ${priorityColors[task.priority]}`}>
          {task.priority}
        </span>
      </div>

      {/* Task Title - Click to open */}
      <h4 className="font-bold text-gray-900 text-sm mb-2 line-clamp-2">{task.title}</h4>

      {/* Tags */}
      {task.tags && task.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {task.tags.slice(0, 3).map((tag, idx) => (
            <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded-full border border-blue-200">
              {tag}
            </span>
          ))}
          {task.tags.length > 3 && (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
              +{task.tags.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Assignee */}
      {task.assignedTo && (
        <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
          <User size={12} />
          <span className="truncate">{task.assignedTo.name}</span>
        </div>
      )}

      {/* Due Date */}
      {task.expectedCompletionDate && (
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <Calendar size={12} />
          <span>{new Date(task.expectedCompletionDate).toLocaleDateString()}</span>
        </div>
      )}

      {/* Approval Badge */}
      {task.requiresApproval && task.approvalStatus && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          <span className={`text-xs font-bold ${
            task.approvalStatus === 'pending' ? 'text-yellow-600' :
            task.approvalStatus === 'approved' ? 'text-green-600' :
            'text-red-600'
          }`}>
            {task.approvalStatus === 'pending' && '⏳ Pending Approval'}
            {task.approvalStatus === 'approved' && '✅ Approved'}
            {task.approvalStatus === 'rejected' && '❌ Rejected'}
          </span>
        </div>
      )}
    </div>
  );
};

interface ColumnProps {
  column: KanbanColumn;
  tasks: DevOpsTask[];
  onTaskClick: (taskId: string) => void;
}

const Column: React.FC<ColumnProps> = ({ column, tasks, onTaskClick }) => {
  const { setNodeRef } = useDroppable({
    id: column.id
  });

  return (
    <div className="flex-1 min-w-[280px] max-w-[350px]">
      <div className={`${column.bgColor} rounded-t-lg px-4 py-3 border-b-4 border-gray-300`}>
        <div className="flex items-center justify-between">
          <h3 className={`font-bold text-sm uppercase ${column.color}`}>{column.title}</h3>
          <span className={`px-2 py-1 ${column.bgColor} border border-gray-300 rounded-full text-xs font-bold ${column.color}`}>
            {tasks.length}
          </span>
        </div>
      </div>
      
      <div 
        ref={setNodeRef}
        className="bg-gray-50 rounded-b-lg p-3 min-h-[500px] max-h-[calc(100vh-300px)] overflow-y-auto"
      >
        <SortableContext items={tasks.map(t => t._id).filter(Boolean) as string[]} strategy={verticalListSortingStrategy}>
          {tasks.map(task => (
            <TaskCard
              key={task._id}
              task={task}
              onClick={() => task._id && onTaskClick(task._id)}
            />
          ))}
          {tasks.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">
              No tasks • Drop here
            </div>
          )}
        </SortableContext>
      </div>
    </div>
  );
};

const KanbanBoard: React.FC<KanbanBoardProps> = ({ currentUser, onTaskClick }) => {
  const [tasks, setTasks] = useState<{ [key: string]: DevOpsTask[] }>({
    backlog: [],
    todo: [],
    'in-progress': [],
    review: [],
    done: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTask, setActiveTask] = useState<DevOpsTask | null>(null);
  const [filters, setFilters] = useState({
    assignee: '',
    priority: '',
    tag: ''
  });
  const [allTags, setAllTags] = useState<string[]>([]);

  useEffect(() => {
    fetchKanbanTasks();
  }, []);

  const fetchKanbanTasks = async () => {
    try {
      setLoading(true);
      // Pass userId and userRole to filter tasks
      const queryParams = `?userId=${currentUser.id}&userRole=${currentUser.role}`;
      const columnPromises = columns.map(async col => {
        const res = await fetch(`/api/tasks/kanban/${col.id}${queryParams}`);
        if (!res.ok) throw new Error(await readApiError(res, 'Failed to load tasks'));
        const data = await res.json();
        return Array.isArray(data) ? (data as DevOpsTask[]) : [];
      });

      const results = await Promise.all(columnPromises);
      const tasksMap: { [key: string]: DevOpsTask[] } = {};

      columns.forEach((col, index) => {
        tasksMap[col.id] = results[index] || [];
      });
      
      setTasks(tasksMap);
      
      // Extract all unique tags
      const tags = new Set<string>();
      Object.values(tasksMap).flat().forEach(task => {
        task.tags?.forEach(tag => tags.add(tag));
      });
      setAllTags(Array.from(tags));
    } catch (error) {
      console.error('Failed to fetch kanban tasks:', error);
      toast(error instanceof Error ? error.message : 'Failed to load tasks', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const taskId = event.active.id as string;
    const task = Object.values(tasks).flat().find(t => t._id === taskId);
    setActiveTask(task || null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over) {
      return;
    }

    const taskId = active.id as string;
    let targetColumn = over.id as string;

    // Check if we dropped on a task (need to find its column)
    const columnIds = columns.map(c => c.id);
    if (!columnIds.includes(targetColumn)) {
      // We dropped on a task, find which column it belongs to
      for (const [col, colTasks] of Object.entries(tasks)) {
        if (colTasks.some(t => t._id === targetColumn)) {
          targetColumn = col;
          break;
        }
      }
    }

    // Find the task and its current column
    let sourceColumn = '';
    let task: DevOpsTask | undefined;
    
    for (const [col, colTasks] of Object.entries(tasks)) {
      task = colTasks.find(t => t._id === taskId);
      if (task) {
        sourceColumn = col;
        break;
      }
    }

    if (!task) {
      return;
    }

    if (sourceColumn === targetColumn) {
      return;
    }

    // Optimistic update (keep the previous board so we can revert exactly)
    const previousTasks = tasks;
    const movedTask = task;
    const newTasks = { ...tasks };
    newTasks[sourceColumn] = newTasks[sourceColumn].filter(t => t._id !== taskId);
    newTasks[targetColumn] = [...(newTasks[targetColumn] || []), movedTask];
    setTasks(newTasks);

    const revert = (message: string) => {
      setTasks(current => {
        // Remove the card from wherever it is now and put it back in its original column/position
        const reverted: { [key: string]: DevOpsTask[] } = {};
        Object.entries(current).forEach(([col, colTasks]) => {
          reverted[col] = colTasks.filter(t => t._id !== taskId);
        });
        reverted[sourceColumn] = previousTasks[sourceColumn] ?? [...(reverted[sourceColumn] || []), movedTask];
        return reverted;
      });
      toast(message, 'error');
    };

    // Update on server
    try {
      const response = await fetch(`/api/tasks/${taskId}/kanban`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kanbanColumn: targetColumn,
          kanbanOrder: newTasks[targetColumn].length - 1
        })
      });

      if (!response.ok) {
        // Server rejected the move (e.g. approval pending, dependency incomplete) — put the card back
        revert(await readApiError(response, 'Could not move task'));
      }
    } catch (error) {
      console.error('Failed to update task:', error);
      revert('Could not move task — network error');
    }
  };

  const getFilteredTasks = (columnTasks: DevOpsTask[]) => {
    return columnTasks.filter(task => {
      if (filters.assignee && task.assignedTo?.id !== filters.assignee) return false;
      if (filters.priority && task.priority !== filters.priority) return false;
      if (filters.tag && !task.tags?.includes(filters.tag)) return false;
      return true;
    });
  };

  const clearFilters = () => {
    setFilters({ assignee: '', priority: '', tag: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const hasActiveFilters = filters.assignee || filters.priority || filters.tag;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Kanban Board</h1>
          <p className="text-gray-600 mt-1">Drag and drop tasks to update their status</p>
        </div>
        <button
          onClick={fetchKanbanTasks}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={20} className="text-gray-600" />
          <h2 className="font-semibold text-gray-900">Filters</h2>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="ml-auto text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
            >
              <X size={16} />
              Clear Filters
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tag</label>
            <select
              value={filters.tag}
              onChange={(e) => setFilters({ ...filters, tag: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Tags</option>
              {allTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Task Count</label>
            <div className="px-3 py-2 bg-gray-100 rounded-lg text-sm font-semibold text-gray-700">
              Total: {Object.values(tasks).flat().length} tasks
            </div>
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <DndContext
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 overflow-x-auto pb-4">
          {columns.map(column => (
            <Column
              key={column.id}
              column={column}
              tasks={getFilteredTasks(tasks[column.id])}
              onTaskClick={onTaskClick}
            />
          ))}
        </div>

        <DragOverlay>
          {activeTask && (
            <div className="bg-white rounded-lg shadow-2xl p-4 border-2 border-blue-400 opacity-90 w-[280px]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-gray-500">#{activeTask.taskNumber}</span>
                <span className={`px-2 py-1 rounded text-xs font-bold uppercase border ${priorityColors[activeTask.priority]}`}>
                  {activeTask.priority}
                </span>
              </div>
              <h4 className="font-bold text-gray-900 text-sm">{activeTask.title}</h4>
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default KanbanBoard;
